<template>
  <div v-if="dialogVisible" class="replenish-mask">
    <div class="replenish-panel">
      <div class="close-icon" @click="close"><Icon type="md-close" size="22"></Icon></div>
      <div class="rp-header">
        <div class="rp-title">大货补货下单</div>
        <div class="rp-sub">{{ product.name || '' }}</div>
        <div class="rp-design" v-if="design">关联款式：{{ design.name || design.style_no || '' }}（{{ patternName }}）</div>
      </div>

      <!-- 阶梯价未配置 -->
      <div v-if="!hasStep" class="rp-unsupported">
        <p>该商品尚未在后台设置「补货阶梯价格」，无法计算补货总价。</p>
      </div>

      <!-- 正常：尺码×颜色数量矩阵 -->
      <div v-else class="rp-body">
        <!-- 客供/定制版型：尺码分组切换 -->
        <div class="rp-groups" v-if="raw_group_list.length && design.pattern_type !== 1">
          <span class="rp-groups-label">尺码组：</span>
          <button
            v-for="(g, gi) in raw_group_list"
            :key="g.id"
            :class="['rp-group-tab', { active: current_group_id === g.id }]"
            @click="switchGroup(g.id)"
          >{{ g.name || g.key || ('组' + (gi + 1)) }}</button>
        </div>

        <table class="rp-grid" v-if="raw_size_list.length && raw_color_list.length">
          <thead>
            <tr>
              <th class="rp-corner">尺码 \ 颜色</th>
              <th v-for="c in raw_color_list" :key="c.color_id">{{ c.color_name || c.color_name_cn || c.color_key }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="sk in raw_size_list" :key="sk">
              <td class="rp-size">{{ sk }}</td>
              <td v-for="c in raw_color_list" :key="c.color_id">
                <input
                  class="rp-qty"
                  type="number"
                  min="0"
                  v-model.number="table_data[sk].color[c.color_id].quantity"
                  placeholder="0"
                />
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else class="rp-unsupported">
          <p>该款式的尺码或颜色数据缺失，无法生成补货矩阵，请前往款式设计页补全后重试。</p>
          <button class="rp-btn rp-ok" @click="goDesign">前往款式设计</button>
        </div>
      </div>

      <div class="rp-footer" v-if="hasStep && raw_size_list.length && raw_color_list.length">
        <div class="rp-sum">
          <span>补货总件数：<b>{{ count }}</b> 件</span>
          <span class="rp-total">补货总价：<b>¥ {{ total }}</b></span>
        </div>
        <div class="rp-actions">
          <button class="rp-btn rp-cancel" @click="close">取 消</button>
          <button class="rp-btn rp-ok" :disabled="submitting || count <= 0" @click="confirm">
            {{ submitting ? '提交中…' : '确认下单并支付' }}
          </button>
        </div>
        <div class="rp-tip">总价已按后台设置的补货阶梯（总件数匹配档位）计算，单价不展示。</div>
      </div>
    </div>
  </div>
</template>

<script>
  import { GetDesignByUUID, GetProduct, CreateOrder } from '@/api/index';

  export default {
    name: 'ReplenishOrder',
    data() {
      return {
        dialogVisible: false,
        submitting: false,
        product: {},
        design: null,
        table_data: {},
        raw_size_list: [],
        raw_color_list: [],
        raw_group_list: [],
        current_group_id: 0,
      };
    },
    computed: {
      hasStep() {
        return !!(this.product.replenish_price_step && this.product.replenish_price_step.length);
      },
      patternName() {
        const map = { 1: '平台版型', 2: '客供版型', 3: '定制版型' };
        return this.design ? (map[this.design.pattern_type] || '未知版型') : '';
      },
      count() {
        let res = 0;
        for (const i in this.table_data) {
          for (const j in this.table_data[i].color) {
            res += parseInt(this.table_data[i].color[j].quantity || 0);
          }
        }
        return res;
      },
      total() {
        const steps = this.product.replenish_price_step || [];
        const unit = this.matchUnitPrice(steps, this.count);
        if (unit === null || unit === undefined || isNaN(unit)) return 0;
        return Math.round(unit * this.count * 100) / 100;
      },
    },
    methods: {
      async show(product) {
        this.product = { ...product };
        this.dialogVisible = true;
        this.submitting = false;
        this.design = null;
        this.table_data = {};
        this.raw_size_list = [];
        this.raw_color_list = [];
        this.raw_group_list = [];
        this.current_group_id = 0;

        // 列表可能未带 replenish_price_step，按需补取详情（id 仍用列表的，详情接口隐藏了 id）
        if (!this.product.replenish_price_step || !this.product.replenish_price_step.length) {
          try {
            const res = await GetProduct({ uuid: this.product.uuid });
            if (res.data && res.data.replenish_price_step) {
              // 用 $set 写入，兼容原对象无该字段（非响应式新增属性）的场景
              this.$set(this.product, 'replenish_price_step', res.data.replenish_price_step);
            }
          } catch (e) {
            // 忽略，confirm 时会提示未配置
          }
        }

        const designUuid =
          this.product.design_uuid || (this.product.design && this.product.design.uuid) || '';
        if (!designUuid) {
          // 查不到关联款式：与 design_id<1 同处理，跳转款式设计
          this.goDesign();
          return;
        }
        try {
          const dres = await GetDesignByUUID({ uuid: designUuid });
          this.design = dres.data;
        } catch (e) {
          // 款式加载失败：与 design_id<1 同处理，跳转款式设计
          this.goDesign();
          return;
        }
        // 全版型支持：按版型构造尺码/颜色/部位矩阵
        this.setupDesign();
      },
      setupDesign() {
        // 颜色：所有版型统一取自 面料A
        const fabricA = (this.design.material || []).find((m) => m.alias === '面料A');
        this.raw_color_list = fabricA && fabricA.color_list ? fabricA.color_list : [];
        this.table_data = {};
        this.raw_group_list = [];
        this.current_group_id = 0;

        if (this.design.pattern_type === 1) {
          // 平台版型：尺码直接来自 pat.size_list，group_id=0
          this.raw_size_list = (this.design.pat && this.design.pat.size_list) || [];
          this.buildSizes(0);
          return;
        }

        // 客供(2)/定制(3)：尺码来自 size_group_list（按性别分组），加尺码组切换
        const sex = (this.design.pattern && this.design.pattern[0] && this.design.pattern[0].sex) || '';
        const groups = (this.design.productcategory && this.design.productcategory.size_group_list) || [];
        const matched = groups.filter((g) => g.key === sex);
        let raw = [];
        matched.forEach((g) => {
          if (g.group && g.group.length) raw = raw.concat(g.group);
        });
        // 性别未匹配到时回退全部分组，避免无尺码可选
        if (!raw.length) {
          groups.forEach((g) => {
            if (g.group && g.group.length) raw = raw.concat(g.group);
          });
        }
        this.raw_group_list = raw;
        if (this.raw_group_list.length) {
          this.current_group_id = this.raw_group_list[0].id;
          const grp = this.raw_group_list[0];
          this.raw_size_list = grp.size_list || [];
          this.buildSizes(this.current_group_id);
        } else {
          this.raw_size_list = [];
        }
      },
      switchGroup(id) {
        this.current_group_id = id;
        const grp = this.raw_group_list.find((v) => v.id === id);
        this.raw_size_list = grp ? (grp.size_list || []) : [];
        this.buildSizes(id);
      },
      // 确保当前尺码集的单元格都存在于 table_data（保留已填数量）
      buildSizes(groupId) {
        for (const size_key of this.raw_size_list) {
          const existing = this.table_data[size_key];
          const cell = this.makeCell(size_key, groupId);
          if (existing && existing.color) {
            for (const cid in cell.color) {
              if (existing.color[cid]) {
                cell.color[cid].quantity = existing.color[cid].quantity || 0;
              }
            }
          }
          this.$set(this.table_data, size_key, cell);
        }
      },
      completeFor(size_key) {
        if (this.design.pattern_type === 3) {
          return (this.design.productcategory && this.design.productcategory.size_info && this.design.productcategory.size_info[size_key]) || {};
        }
        // 平台(1)/客供(2) 均取 pat.size_info
        return (this.design.pat && this.design.pat.size_info && this.design.pat.size_info[size_key]) || {};
      },
      makeCell(size_key, groupId) {
        const temp_color = {};
        for (const c of this.raw_color_list) {
          temp_color[c.color_id] = { ...c, quantity: 0 };
        }
        const temp_complete = this.$_.cloneDeep(this.completeFor(size_key));
        for (const i in temp_complete) {
          if (!this.$_.has(temp_complete[i], 'val')) temp_complete[i].val = temp_complete[i].normal;
        }
        return {
          size: { size_key: size_key, size_alias: size_key },
          color: temp_color,
          complete: temp_complete,
          sort: this.raw_size_list.indexOf(size_key),
          group_id: groupId,
        };
      },
      matchUnitPrice(steps, qty) {
        if (!steps || !steps.length || qty <= 0) return null;
        // 区间格式：命中 from_qty <= qty <= to_qty（to_qty 为空或 0 表示上不封顶）
        const hasRange = steps.some((s) => s.from_qty !== undefined || s.to_qty !== undefined);
        if (hasRange) {
          for (const s of steps) {
            if (s.from_qty === undefined) continue;
            const from = Number(s.from_qty) || 0;
            let to = s.to_qty === null || s.to_qty === undefined || s.to_qty === '' ? null : Number(s.to_qty);
            if (to !== null && !(to > 0)) to = null;
            if (qty >= from && (to === null || qty <= to)) {
              const price = Number(s.price);
              return isNaN(price) ? null : price;
            }
          }
          return null;
        }
        // 兼容旧档位格式
        const sorted = [...steps].sort((a, b) => (a.quantity || 0) - (b.quantity || 0));
        let unit = null;
        for (const s of sorted) {
          if (qty >= (s.quantity || 0)) unit = s.price;
        }
        if (unit === null || unit === undefined) unit = sorted[0].price;
        const price = Number(unit);
        return isNaN(price) ? null : price;
      },
      async confirm() {
        if (this.count <= 0) {
          this.$layer.msg('请填写补货数量');
          return;
        }
        if (!this.hasStep) {
          this.$layer.msg('该商品未配置补货阶梯价格');
          return;
        }
        if (!this.total) {
          this.$layer.msg('补货数量未匹配到价格区间，请调整补货数量');
          return;
        }
        this.submitting = true;
        const param = {
          from: 'design',
          uuid: this.design.uuid,
          new_style_no: '',
          form_data: this.table_data,
          color_list: this.raw_color_list,
          form_data_multiple: {},
          color_list_multiple: {},
          sample_extra: {},
          form_data_packaging: {},
          user_fields: {},
          is_fast: '',
          brand_id: 0,
          is_tech_real: 0,
          is_try_pattern: 0,
          is_hot_print_sample: 0,
          is_replenish: 1,
          // 列表行/订单product子对象不一定带 id，uuid 恒定存在，兜底避免 product_id 落空导致取不到阶梯价
          product_id: this.product.id || this.product.uuid,
        };
        CreateOrder(param)
          .then((res) => {
            this.$layer.msg('下单成功，正在跳转支付…');
            this.dialogVisible = false;
            this.$router.replace({ name: 'WorkBenchCheckout', query: { uuid: res.data.uuid } });
          })
          .catch((err) => {
            this.$layer.msg((err && err.msg) || '下单失败');
          })
          .finally(() => {
            this.submitting = false;
          });
      },
      goDesign() {
        this.dialogVisible = false;
        this.$router.push({
          name: 'WorkBenchDesign',
          query: {
            from: 'restock',
            cover: this.product.image || this.product.product_image || '',
            parent_product_category_id: this.product.parent_product_category_id,
            child_product_category_id: this.product.child_product_category_id,
          },
        });
      },
      close() {
        this.dialogVisible = false;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .replenish-mask {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .replenish-panel {
    position: relative;
    width: 720px;
    max-width: 92vw;
    max-height: 88vh;
    overflow: auto;
    background: #fff;
    border-radius: 8px;
    padding: 20px 24px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  }
  .close-icon {
    position: absolute;
    top: 14px;
    right: 16px;
    cursor: pointer;
    line-height: 1;
    color: #909399;
  }
  .close-icon:hover {
    color: #606266;
  }
  .rp-header {
    border-bottom: 1px solid #eee;
    padding-bottom: 12px;
    margin-bottom: 14px;
  }
  .rp-title {
    font-size: 17px;
    font-weight: 600;
    color: #303133;
  }
  .rp-sub {
    margin-top: 6px;
    font-size: 13px;
    color: #606266;
  }
  .rp-design {
    margin-top: 4px;
    font-size: 12px;
    color: #909399;
  }
  .rp-groups {
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  .rp-groups-label {
    font-size: 13px;
    color: #606266;
  }
  .rp-group-tab {
    border: 1px solid #dcdfe6;
    background: #fff;
    color: #606266;
    border-radius: 4px;
    padding: 5px 14px;
    font-size: 13px;
    cursor: pointer;
  }
  .rp-group-tab.active {
    background: #409eff;
    border-color: #409eff;
    color: #fff;
  }
  .rp-grid {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  .rp-grid th,
  .rp-grid td {
    border: 1px solid #ebeef5;
    padding: 8px 6px;
    text-align: center;
  }
  .rp-corner {
    background: #fafafa;
    white-space: nowrap;
  }
  .rp-size {
    background: #fafafa;
    font-weight: 600;
  }
  .rp-qty {
    width: 64px;
    padding: 5px 6px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    text-align: center;
    outline: none;
  }
  .rp-qty:focus {
    border-color: #409eff;
  }
  .rp-footer {
    margin-top: 16px;
    border-top: 1px solid #eee;
    padding-top: 14px;
  }
  .rp-sum {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    color: #303133;
  }
  .rp-total {
    color: #f56c6c;
    font-size: 16px;
  }
  .rp-total b {
    font-size: 20px;
  }
  .rp-actions {
    margin-top: 14px;
    text-align: right;
  }
  .rp-btn {
    border: 1px solid transparent;
    border-radius: 4px;
    padding: 8px 18px;
    font-size: 14px;
    cursor: pointer;
  }
  .rp-cancel {
    background: #fff;
    color: #ff5967;
    border-color: #ff5967;
    margin-right: 10px;
  }
  .rp-cancel:hover {
    background: #fff0f1;
  }
  .rp-ok {
    background: #ff5967;
    color: #fff;
    border-color: #ff5967;
  }
  .rp-ok:hover {
    background: #ff4357;
    border-color: #ff4357;
  }
  .rp-ok:disabled {
    background: #ffb3ba;
    border-color: #ffb3ba;
    cursor: not-allowed;
  }
  .rp-tip {
    margin-top: 10px;
    font-size: 12px;
    color: #909399;
  }
  .rp-unsupported {
    padding: 24px 0;
    text-align: center;
    color: #909399;
    font-size: 14px;
  }
  .rp-unsupported p {
    margin-bottom: 14px;
  }
</style>
